import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  container: {
    height: 'calc(100vh - 66px)',
    overflowY: 'auto',
    padding: '24px 30px',
    backgroundColor: '#ffffff',
    color: '#12202f',
    boxSizing: 'border-box',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#e4e6eb',
      borderRadius: '4px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px'
    }
  },
  groupSection: {
    marginBottom: '32px',
  },
  groupHeader: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#0066cc',
    borderBottom: '1px solid #e4e6eb',
    paddingBottom: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  imageCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e4e6eb',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#0066cc',
      boxShadow: '0 8px 24px rgba(0, 102, 204, 0.12)',
      transform: 'translateY(-2px)',
    },
    '&:hover $hoverOverlay': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    backgroundColor: '#f0f2f5',
    overflow: 'hidden',
  },
  cardImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  hoverOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 8px',
    backgroundColor: 'rgba(0,0,0,0.42)',
    opacity: 0,
    transition: 'opacity 0.18s ease',
    pointerEvents: 'none',
  },
  overlayBtn: {
    color: '#ffffff',
    background: 'transparent',
    padding: 4,
    '&:hover': {
      background: 'transparent'
    }
  },
  timeBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: 'rgba(255,255,255,0.92)',
    color: '#12202f',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
    border: '1px solid #e4e6eb',
    whiteSpace: 'nowrap',
  },
  cameraBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: '#9e9e9e',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
  },
  overlayCameraPill: {
    backgroundColor: 'rgba(158,158,158,0.9)',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 4px',
    borderRadius: '8px',
    lineHeight: 1,
    letterSpacing: '0.2px'
  },
  cardDetails: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    backgroundColor: '#ffffff',
    flex: 1,
  },
  cardTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#12202f',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSub: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '11px',
    color: '#65676b',
    // allow address to wrap to multiple lines
    overflow: 'hidden',
  },
  cardSubIcon: {
    width: '13px',
    height: '13px',
    flexShrink: 0,
  },
  cardSubText: {
    fontSize: '11px',
    color: '#65676b',
    // allow up to 2 lines for address
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  emptyBox: {
    height: 'calc(100vh - 66px)',
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '16px'
    }
  },
  emptyContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const ImageCard = React.memo(({ image, index, onZoom, onDownload }) => {
  const classes = useStyles({});

  const handleZoom = e => {
    e.stopPropagation();
    onZoom(index);
  };

  const handleDownload = e => {
    e.stopPropagation();
    onDownload(image.url, image.download_url);
  };

  return (
    <Box className={classes.imageCard} onClick={() => onZoom(index)}>
      <Box className={classes.imageWrapper}>
        <img
          src={image.thumbnailUrl}
          alt={image.file_name}
          className={classes.cardImage}
          loading="lazy"
        />

        <Box className={classes.hoverOverlay}>
          <Box>
            {image.camera && (
              <span className={classes.overlayCameraPill}>
                {image.camera.replace('Camera ', 'Cam ')}
              </span>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              className={classes.overlayBtn}
              onClick={handleDownload}
              size="small"
              aria-label="Tải xuống"
              title="Tải xuống"
            >
              <GetAppIcon style={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              className={classes.overlayBtn}
              onClick={handleZoom}
              size="small"
              aria-label="Xem ảnh lớn"
              title="Xem ảnh lớn"
            >
              <FullscreenIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <Box className={classes.cardDetails}>
        <Typography className={classes.cardTitle} title={image.created_at}>
          {image.created_at || '--'}
        </Typography>
        <Box className={classes.cardSub}>
          <img
            src="/static/iconSvg/iconLocationRed.svg"
            alt="location"
            className={classes.cardSubIcon}
          />
          <span className={classes.cardSubText} title={image.device?.location}>
            {image.device?.location || '--'}
          </span>
        </Box>
      </Box>
    </Box>
  );
});

ImageCard.propTypes = {
  image: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onZoom: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

const DEFAULT_CENTER = [21.024683, 105.832495];

Result.propTypes = {
  isShowResult: PropTypes.bool.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired
};

function Result({ isShowResult, isSidebarOpen, onCloseSidebar }) {
  const classes = useStyles({ isSidebarOpen });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpenImage, setIsOpenImage] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [dataPhotoView, setDataPhotoView] = useState({});
  const [visibleCount, setVisibleCount] = useState(40);

  const listVehicleTracking = useSelector(
    state => state.vehicleSlice?.listVehicleTracking || []
  );

  const listLicensePlateCamera = useSelector(
    state => state.vehicleSlice?.listLicensePlateCamera || []
  );

  const rawImages = useSelector(
    state => state.imageSlice?.listImage || []
  );
  // Error message set by imageSlice.rejected — surfaced in the empty state
  // below so a failed search (token expired, network, etc.) doesn't silently
  // fall back to the previous result.
  const imageErr = useSelector(state => state.imageSlice?.err);
  const sortedImages = (rawImages || []).slice().sort((a, b) => {
    const getTime = (img) => {
      if (img.capture_time) return moment.utc(img.capture_time).valueOf();
      if (img.created_at) return moment(img.created_at).valueOf();
      if (img.device_time) return moment.unix(img.device_time).valueOf();
      return 0;
    };
    return getTime(b) - getTime(a);
  });

  const listImages = sortedImages.map(image => {
    const actualSerial = image.serial || image.tenant;
    const vehicleInfo = listVehicleTracking.find(
      v => v.device_serial === actualSerial
    ) || listLicensePlateCamera.find(
      v => v.serial === actualSerial
    );
    let cameraDisplay = image.camera || `Camera ${image.camera_num || '--'}`;
    if (image.camera) {
      const camStr = image.camera.toString();
      const cameraMatch = camStr.match(/CAM(\d+)/i);
      if (cameraMatch) {
        cameraDisplay = `Camera ${cameraMatch[1]}`;
      } else if (camStr.length === 3 && camStr.endsWith('01')) {
        cameraDisplay = `Camera ${camStr.charAt(0)}`;
      } else if (['1', '2', '3', '4'].includes(camStr)) {
        cameraDisplay = `Camera ${camStr}`;
      } else {
        cameraDisplay = `Camera ${camStr}`;
      }
    }

    let displayTime = '--';
    const captureTime = moment.utc(image.capture_time);

    if (image.capture_time && captureTime.isValid()) {
      displayTime = captureTime.add(7, 'hours').format('DD-MM-YYYY HH:mm:ss');
    }

    if (displayTime === '--') {
      if (image.created_at && moment(image.created_at).isValid()) {
        displayTime = moment(image.created_at).add(7, 'hours').format('DD-MM-YYYY HH:mm:ss');
      } else if (image.device_time) {
        const deviceTime = moment.unix(image.device_time);
        if (deviceTime.isValid()) {
          displayTime = deviceTime.add(7, 'hours').format('DD-MM-YYYY HH:mm:ss');
        }
      }
    }

    return {
      id: image.id || image.filename,
      url: image.url || '/static/default-image.jpg',
      thumbnailUrl: image.url || '/static/default-image.jpg',
      download_url: image.download_url || image.url || '',
      file_name: image.filename || image.file_name,
      created_at: displayTime,
      camera: cameraDisplay,
      hourKey: displayTime !== '--' ? displayTime.slice(9, 11) : '--', // hour extracted from DD-MM-YYYY HH:mm:ss
      device: {
        vehicle: {
          license_plate: vehicleInfo?.license_plate || actualSerial || '--'
        },
        location: image.location || '--',
        position: {
          lat: parseFloat(image.lat) || DEFAULT_CENTER[0],
          lng: parseFloat(image.lng) || DEFAULT_CENTER[1]
        },
        device_status: image.device?.status || '--',
        direction: 0
      }
    };
  });

  const groupImagesByHour = useCallback((imgs) => {
    const groups = {};
    imgs.forEach((img, idx) => {
      let hour = '--';
      if (img.created_at && img.created_at !== '--') {
        const parts = img.created_at.split(' ');
        if (parts[1]) {
          hour = parts[1].slice(0, 2);
        }
      }
      if (!groups[hour]) groups[hour] = [];
      groups[hour].push({ ...img, _originalIndex: idx });
    });
    return groups;
  }, []);

  useEffect(() => {
    setVisibleCount(40);
  }, [rawImages]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (visibleCount < listImages.length) {
        setVisibleCount(prev => Math.min(prev + 40, listImages.length));
      }
    }
  };

  const visibleImages = listImages.slice(0, visibleCount);
  const groupedImages = groupImagesByHour(visibleImages);
  const sortedHours = Object.keys(groupedImages).sort((a, b) => {
    if (a === '--') return 1;
    if (b === '--') return -1;
    return Number(b) - Number(a);
  });

  /**
   * Reuse the SAME presigned URL the <img> already loaded, so the browser
   * cache serves the bytes locally instead of round-tripping to storage a
   * second time with a different presign. Storage CPU drops by 1 HMAC sign
   * per actual download, and bandwidth drops by 1 GET per download.
   *
   * Falls back to the legacy `downloadUrl` only if `imageUrl` is missing
   * (defensive — list response always includes `url`).
   */
  const handleDownload = async (imageUrl, downloadUrl) => {
    const targetUrl = imageUrl || downloadUrl;
    if (!targetUrl) return;
    const fileName = targetUrl.split('/').pop().split('?')[0] || 'image.jpg';
    try {
      // Same URL as <img src={image.url}> → browser cache hit, no storage call.
      const response = await fetch(targetUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(targetUrl, '_blank');
    }
  };

  const handleZoom = (index) => {
    setPhotoIndex(index);
    setDataPhotoView(listImages[index] || {});
    setIsOpenImage(true);
  };

  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);

  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2500);
  }, []);

  useEffect(() => {
    if (isOpenImage) {
      resetControlsTimer();
      const handleMouseMove = () => {
        resetControlsTimer();
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleMouseMove);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchmove', handleMouseMove);
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      };
    }
  }, [isOpenImage, resetControlsTimer]);

  return (
    <>
      {isShowResult && (
        <>
          {listImages.length === 0 ? (
            <Box className={classes.emptyBox}>
              <Box className={classes.emptyContent}>
                <Box>
                  <img src="/static/empty.png" alt="empty" />
                </Box>
                <Typography>
                  <h4>{imageErr ? imageErr : 'Danh sách trống'}</h4>
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box className={classes.container} onScroll={handleScroll}>
              {sortedHours.map(hour => {
                const imgsInHour = groupedImages[hour];
                const hourLabel =
                  hour !== '--'
                    ? `${hour.padStart(2, '0')}:00 – ${(Number(hour) + 1).toString().padStart(2, '0')}:00 (${imgsInHour.length} ảnh)`
                    : `Không xác định giờ (${imgsInHour.length} ảnh)`;

                return (
                  <Box className={classes.groupSection} key={hour}>
                    <Typography className={classes.groupHeader}>
                      <img
                        src="/static/iconSvg/iconTime.svg"
                        alt="time"
                        style={{ width: '16px', height: '16px' }}
                      />
                      {hourLabel}
                    </Typography>
                    <Grid container spacing={2}>
                      {imgsInHour.map(image => (
                        <Grid
                          item
                          key={image.id}
                          lg={3}
                          md={4}
                          sm={6}
                          xs={12}
                        >
                          <ImageCard
                            image={image}
                            index={image._originalIndex}
                            onZoom={handleZoom}
                            onDownload={handleDownload}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}

      {isOpenImage && (
        <div className={showControls ? "" : "lb-controls-hidden"}>
          <Lightbox
            images={listImages}
            startIndex={photoIndex}
            onClose={() => setIsOpenImage(false)}
          />
          <Box
            className="lb-custom-left-header"
            style={{
              position: 'fixed',
              top: '0px',
              left: isMobile ? '10px' : '30px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              zIndex: 99999,
              pointerEvents: 'none',
            }}
          >
            <Box style={{ display: 'flex', gap: '15px', alignItems: 'center', pointerEvents: 'auto' }}>
              <Box style={{ cursor: 'pointer' }}>
                <img src="/static/iconSvg/image-active-icon.svg" alt="Image" />
              </Box>
              <Box>
                <Typography style={{ color: '#ffffff', fontSize: '14px', fontWeight: 500 }}>
                  {dataPhotoView.created_at}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            className="custom-download-btn"
            onClick={() => handleDownload(dataPhotoView.url, dataPhotoView.download_url)}
            title="Tải xuống"
          >
            <img
              style={{ width: '20px', height: '20px' }}
              src="/static/iconSvg/downloadIcon.svg"
              alt="Download"
            />
          </Box>
        </div>
      )}
    </>
  );
}

export default Result;
